/**
 * Automatische Bild-Verkleinerung für Dokument-Uploads.
 *
 * Die Behörde (USP) nimmt Dokumente nur bis 2 MB an — Handy-Fotos von
 * Ausweisen sind aber oft 5–10 MB. Statt das Mitglied mit „Datei zu groß"
 * abzuweisen, verkleinern wir Bilder direkt im Browser: schrittweise
 * kleinere Kantenlänge und JPEG-Qualität, bis die Datei sicher unter dem
 * Limit liegt. Untergrenzen halten das Dokument lesbar (die Behörde prüft
 * die MRZ-Zeile!): längste Kante nie unter 1600 px, Qualität nie unter 0.5.
 *
 * PDFs können im Browser nicht verkleinert werden — dafür gibt es einen
 * verständlichen Hinweis (Foto statt PDF hochladen).
 */

const ZIEL_BYTES = 1_900_000; // Sicherheitsmarge unter dem 2-MB-Behördenlimit
const KANTEN = [3000, 2600, 2200, 1900, 1600];
const QUALITAETEN = [0.85, 0.75, 0.65, 0.55, 0.5];

export interface KompressionsErgebnis {
  file: File;
  verkleinert: boolean;
  /** Gesetzt, wenn NICHT unter 2 MB gebracht werden konnte (PDF, Exot). */
  problem?: string;
}

async function ladeBild(file: File): Promise<ImageBitmap | HTMLImageElement> {
  try {
    // from-image: EXIF-Drehung von Handy-Fotos wird korrekt angewendet
    // (Options-Cast: ältere TS-DOM-Typen kennen 'from-image' noch nicht)
    return await createImageBitmap(file, { imageOrientation: 'from-image' } as any);
  } catch {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Bild nicht lesbar')); };
      img.src = url;
    });
  }
}

/**
 * canvas.toBlob liefert auf iOS-Safari bei großen Bildern gelegentlich `null`
 * (Canvas-Speicherlimit). Dann über toDataURL ausweichen -- das ist auf iOS
 * zuverlässiger und liefert nie null (wirft höchstens). So schlägt die
 * Verkleinerung am Handy nicht mehr fälschlich mit „Datei zu groß" fehl.
 */
function canvasZuBlob(canvas: HTMLCanvasElement, qualitaet: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob) { resolve(blob); return; }
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', qualitaet);
          const bin = atob(dataUrl.split(',')[1]);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          resolve(new Blob([arr], { type: 'image/jpeg' }));
        } catch {
          resolve(null);
        }
      },
      'image/jpeg',
      qualitaet,
    );
  });
}

function alsJpeg(bild: ImageBitmap | HTMLImageElement, maxKante: number, qualitaet: number): Promise<Blob | null> {
  const b = bild.width > bild.height ? bild.width : bild.height;
  const faktor = b > maxKante ? maxKante / b : 1;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bild.width * faktor);
  canvas.height = Math.round(bild.height * faktor);
  const ctx = canvas.getContext('2d')!;
  // Weißer Grund: PNG-Transparenz darf im JPEG nicht schwarz werden
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bild, 0, 0, canvas.width, canvas.height);
  return canvasZuBlob(canvas, qualitaet);
}

export async function komprimiereBild(file: File): Promise<KompressionsErgebnis> {
  // iPhone-Fotos kommen oft als HEIC/HEIF — die Behörde (und unser Server)
  // nimmt nur JPEG/PNG/PDF. Solche Bilder IMMER nach JPEG wandeln, auch wenn
  // sie klein genug sind (Fall Moroder 03.09.2026: Uploads scheiterten am
  // iPhone). Safari kann HEIC dekodieren, der Canvas macht JPEG daraus.
  const istBild = file.type.startsWith('image/');
  const brauchtKonvertierung = istBild && !['image/jpeg', 'image/png'].includes(file.type);
  if (file.size <= ZIEL_BYTES && !brauchtKonvertierung) {
    return { file, verkleinert: false };
  }
  if (!istBild) {
    return {
      file,
      verkleinert: false,
      problem:
        'Dieses PDF ist größer als 2 MB (Behörden-Limit) und kann im Browser nicht '
        + 'verkleinert werden. Einfachste Lösung: Fotografiere die Seiten einzeln mit dem '
        + 'Handy und lade die Fotos hoch — Fotos verkleinern wir automatisch.',
    };
  }
  let bild;
  try {
    bild = await ladeBild(file);
  } catch {
    return { file, verkleinert: false, problem: 'Bild konnte nicht gelesen werden.' };
  }
  const neuerName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  let kleinstes: Blob | null = null;
  try {
    for (const kante of KANTEN) {
      for (const q of QUALITAETEN) {
        const blob = await alsJpeg(bild, kante, q);
        if (!blob) continue;
        if (kleinstes === null || blob.size < kleinstes.size) kleinstes = blob;
        if (blob.size <= ZIEL_BYTES) {
          return {
            file: new File([blob], neuerName, { type: 'image/jpeg' }),
            verkleinert: true,
          };
        }
      }
    }
  } finally {
    // ImageBitmap hält Speicher/GPU-Ressourcen -- am Handy freigeben, damit
    // die Verkleinerung des zweiten Bilds (Ausweis-Rückseite) genug Speicher hat.
    if (typeof (bild as ImageBitmap).close === 'function') (bild as ImageBitmap).close();
  }
  if (kleinstes && kleinstes.size <= 2048000) {
    return { file: new File([kleinstes], neuerName, { type: 'image/jpeg' }), verkleinert: true };
  }
  return {
    file,
    verkleinert: false,
    problem: 'Das Bild konnte nicht unter 2 MB verkleinert werden — bitte am Handy in den Kamera-Einstellungen eine geringere Auflösung wählen oder ein neues, kleineres Foto machen.',
  };
}
